import { ROUTE } from '../config'
import { isOffline } from '../services/offlineDetect'
import {
  getDownloadedCategories,
  getOfflineDownloadState,
} from '../services/offlineStorage'
import {
  findOfflineVehicleForRoute,
  loadOfflineGuideVehicle,
  resolveBrandRouteFromBaseRoute,
} from './loadOfflineGuideVehicle'

/**
 * Returns whether Redux already has publication data loaded
 *
 * @param {object} contents Redux contents slice
 * @returns {boolean}
 */
function hasLoadedPublications(contents) {
  const { publications } = contents
  if (!publications) return false
  if (typeof publications.size === 'number') return publications.size > 0
  if (Array.isArray(publications)) return publications.length > 0
  return true
}

/**
 * Loads only offline category metadata when Guide has already hydrated content
 *
 * @param {string} baseRoute guide base route
 * @param {string} brandRoute brand route prefix
 * @param {string} currentBrand active brand name
 * @returns {Promise<object|null>}
 */
export async function loadBrowseCategoriesOnly(baseRoute, brandRoute, currentBrand) {
  const record = await findOfflineVehicleForRoute({ baseRoute, brandRoute, currentBrand })
  if (!record) return null

  const vehicleId = String(record.vehicleId)
  const [downloadedCategories, downloadState] = await Promise.all([
    getDownloadedCategories(vehicleId),
    getOfflineDownloadState(vehicleId),
  ])

  return {
    ok: true,
    mode: 'offline',
    vehicleId,
    downloadedCategories,
    downloadState,
  }
}

/**
 * Initializes the browse page for the current connectivity mode.
 * Online: no storage reads — Guide has already loaded API content.
 * Offline: hydrates Redux from IndexedDB before browse UI renders.
 *
 * @param {object} options loader options
 * @param {string} options.baseRoute guide base route
 * @param {string} options.brandRoute brand route prefix
 * @param {object} options.vehicle Redux vehicle slice
 * @param {object} options.contents Redux contents slice
 * @param {Function} options.dispatch Redux dispatch
 * @param {object} options.vehicleActions vehicle action creators
 * @returns {Promise<object>} browse page load result
 */
export async function loadBrowsePage({
  baseRoute,
  brandRoute,
  vehicle,
  contents,
  dispatch,
  vehicleActions,
}) {
  if (!isOffline()) {
    return { ok: true, mode: 'online' }
  }

  const routeBrand = brandRoute || resolveBrandRouteFromBaseRoute(baseRoute)

  if (hasLoadedPublications(contents)) {
    const categoriesResult = await loadBrowseCategoriesOnly(baseRoute, routeBrand, vehicle.currentBrand)
    if (categoriesResult) {
      return categoriesResult
    }
  }

  const result = await loadOfflineGuideVehicle({
    dispatch,
    vehicleActions,
    vehicle,
    contents,
    baseRoute,
    brandRoute: routeBrand,
  })

  if (!result.ok) {
    return { ok: false, redirect: ROUTE.OFFLINE, empty: result.empty === true }
  }

  const hasMenuContent =
    result.downloadedCategories.size > 0 ||
    hasLoadedPublications(contents) ||
    (result.snapshot && (result.snapshot.publications?.length || result.snapshot.topics?.length))

  if (!hasMenuContent) {
    return { ok: false, redirect: ROUTE.OFFLINE }
  }

  return {
    ok: true,
    mode: 'offline',
    vehicleId: result.vehicleId,
    downloadedCategories: result.downloadedCategories,
    downloadState: result.downloadState,
    ownersManualPdfs: result.ownersManualPdfs,
  }
}
